import { OneTimePersonDto } from '@api/models/invitation/OneTimePersonDto';
import { companyService } from '@api/services/services';
import { VisitationAddOneTimeConflictData } from '../visitation/VisitationAddModalOneTimeConflict';
import { toOneTimePersonDto } from './oneTimePerson';

export interface ResolveVisitorConflictsResult {
  persons: OneTimePersonDto[];
  supplierId: string | undefined;
}

/**
 * Reconciles form visitors against existing supplier-employee records (by email).
 * Prompts the caller to resolve any name/lastName/phone mismatch; cancelling aborts the batch.
 * Returns the (possibly updated) visitors and the supplier id when exactly one match was found.
 */
export const resolveVisitorConflicts = async (
  persons: OneTimePersonDto[],
  requestResolution: (conflict: VisitationAddOneTimeConflictData<OneTimePersonDto>) => Promise<OneTimePersonDto | undefined>,
): Promise<ResolveVisitorConflictsResult | undefined> => {
  const resolved = [...persons];
  const supplierIds = new Set<string>();
  for (let i = 0; i < resolved.length; i++) {
    const visitor = resolved[i];
    if (!visitor.email) continue;
    try {
      const res = await companyService.findSupplierEmployeeInCompany({ email: visitor.email });
      const db = res.data;
      if (!db) continue;
      if (db.supplier?.id) supplierIds.add(db.supplier.id);
      const formPhone = visitor.phone ? visitor.phone : undefined;
      const dbPhone = db.phone ? db.phone : undefined;
      const isSame = visitor.name === db.name && visitor.lastName === db.lastName && formPhone === dbPhone;
      if (isSame) continue;
      const merged = await requestResolution({ form: visitor, db });
      if (!merged) return undefined;
      resolved[i] = toOneTimePersonDto(merged);
    } catch {
      // skip visitor on lookup failure
    }
  }
  return {
    persons: resolved,
    supplierId: supplierIds.size === 1 ? [...supplierIds][0] : undefined,
  };
};
